import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { RESOURCE_SECTIONS } from '@/constants/resourceSections';
import { ArrowLeft } from 'lucide-react';
import { ProductCard } from '@/components/digital/ProductCard';
import { BlueprintFeatureCard } from '@/components/founder/sales/BlueprintFeatureCard';
import { digitalApi } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full border text-[10px] font-label tracking-widest uppercase transition-colors duration-300 ${
      active ? 'bg-matte text-ivory border-matte' : 'border-taupe/40 text-matte/70 hover:border-matte hover:text-matte'
    }`}
  >
    {children}
  </button>
);

export default function DigitalProducts() {
  const { section = 'digital-products' } = useParams();
  const cfg = RESOURCE_SECTIONS[section];
  usePageMeta({
    title: section === 'digital-products' ? 'Digital Products Library' : cfg?.label || 'Resources',
    description: cfg?.metaDescription,
    path: `/resources/${section}`,
  });
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ format: 'all', category: 'all', price: 'all' });

  useEffect(() => {
    setData(null);
    setFilter({ format: 'all', category: 'all', price: 'all' });
    if (!cfg || section === 'journal') return;
    digitalApi.list({ section }).then(setData).catch(() => setData({ items: [], categories: [], formats: [] }));
  }, [section, cfg]);


  const items = useMemo(() => (data?.items || []).filter((p) =>
    (filter.format === 'all' || p.format === filter.format)
    && (filter.category === 'all' || p.category_id === filter.category)
    && (filter.price === 'all' || (p.content_type !== 'article' && (filter.price === 'free' ? p.is_free : !p.is_free)))), [data, filter]);
  const pick = (k, v) => () => setFilter((f) => ({ ...f, [k]: v }));
  if (section === 'journal') return <Navigate to="/journal" replace />;
  if (!cfg) return <Navigate to="/resources" replace />;
  const hasDownloads = (data?.items || []).some((p) => p.content_type !== 'article');

  return (
    <div className="bg-ivory" data-testid="digital-products-page" data-section={section}>
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Link to="/resources" className="btn-ghost mb-10" data-testid="digital-back-to-resources">
              <ArrowLeft size={14} strokeWidth={1.6} /> Resources
            </Link>
            <div className="eyebrow !text-gold mb-6" data-testid="section-eyebrow">{cfg.eyebrow}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[7rem] leading-[0.94] tracking-tightest text-matte" data-testid="section-title">
              {cfg.title}
              <br />
              <em className="italic text-taupe">{cfg.accent}</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">{cfg.intro}</p>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-wrap gap-3 pb-10 border-b border-taupe/30" data-testid="digital-filters">
            {hasDownloads && (<>
            <Chip active={filter.price === 'all'} onClick={pick('price', 'all')} testid="digital-filter-price-all">All</Chip>
            <Chip active={filter.price === 'free'} onClick={pick('price', 'free')} testid="digital-filter-price-free">Free</Chip>
            <Chip active={filter.price === 'paid'} onClick={pick('price', 'paid')} testid="digital-filter-price-paid">Paid</Chip>
            </>)}
            {(data?.formats || []).length > 1 && <span className="w-px bg-taupe/30 mx-1" aria-hidden />}
            {(data?.formats || []).length > 1 && data.formats.map((f) => (
              <Chip key={f} active={filter.format === f} onClick={pick('format', filter.format === f ? 'all' : f)} testid={`digital-filter-format-${f.toLowerCase()}`}>{f}</Chip>
            ))}
            {(data?.categories || []).length > 1 && <span className="w-px bg-taupe/30 mx-1" aria-hidden />}
            {(data?.categories || []).length > 1 && data.categories.map((c) => (
              <Chip key={c.id} active={filter.category === c.id} onClick={pick('category', filter.category === c.id ? 'all' : c.id)} testid={`digital-filter-category-${c.id}`}>{c.name}</Chip>
            ))}
          </div>

          {section === 'digital-products' && filter.price !== 'free' && <BlueprintFeatureCard />}
          {!data && <p className="py-24 text-matte/50" data-testid="digital-loading">Loading the library…</p>}
          {data && items.length === 0 && (
            <div className="py-24 max-w-xl" data-testid="digital-empty">
              <p className="font-serif-display text-3xl md:text-4xl tracking-tighter text-matte">New releases are being prepared.</p>
              <p className="mt-4 text-matte/60">Check back soon, or join The Founder&rsquo;s List to hear first.</p>
              <Link to="/founders-list" className="btn-ghost mt-8" data-testid="digital-empty-founders-list">Join The Founder&rsquo;s List</Link>
            </div>
          )}
          {items.length > 0 && (
            <div className="pt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="digital-products-grid">
              {items.map((p) => <ProductCard key={p.id} product={p} section={section} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
